import {Component, computed, DestroyRef, effect, inject, OnInit, signal, untracked, ChangeDetectionStrategy} from '@angular/core';
import {UnavailableValueComponent} from 'src/app/modules/design-system/unavailable-value/unavailable-value.component';
import {ErrorStateComponent} from 'src/app/modules/design-system/error-state/error-state.component';
import {LoadError, toLoadError} from 'src/app/services/shared/load-error.model';
import * as Bowser from 'bowser';
import {Router} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {MenuState, MenuStateService} from 'src/app/services/ui/menu-state.service';
import {LayoutStore} from 'src/app/services/ui/layout.store';
import {Permission} from 'src/app/services/auth/permissions.service';
import {FeatureFlagService} from 'src/app/services/feature-flags/feature-flag.service';
import {FeatureFlag} from 'src/app/models/feature-flags.model';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import * as AppUrl from 'src/app/models/home/navigation.model';
import {
	ANNOUNCEMENT_URL,
	APPLICATIONS_URL,
	ARCHITECTURE_URL,
	TECHNOLOGY_URL,
	PRISM_URL,
	DATA_URL,
	DIAGRAMS_URL,
	EXTENSION_URL,
	FINANCE_URL,
	HOME_URL,
	INFRASTRUCTURE_NETWORKS_URL,
	INFRASTRUCTURE_SERVERS_URL,
	INFRASTRUCTURE_URL,
	INTERCOM_URL,
	OPERATION_URL,
	ORGANIZATION_URL,
	SETTINGS_ACCOUNT_URL,
	SETTINGS_URL,
	USAGE_URL,
	USERS_USERS_URL,
	DEVICES_URL,
	DEVICES_WORKSTATIONS_URL,
	WORKSTATIONS_DASHBOARD_URL,
	CUSTOM_DASHBOARDS_URL
} from 'src/app/models/home/navigation.model';
import {IntercomService} from 'src/app/services/ui/intercom.service';
import {UserMenuCardComponent} from 'src/app/modules/global/user-menu-card/user-menu-card.component';
import {TenantSwitcherCardComponent} from 'src/app/modules/global/tenant-switcher-card/tenant-switcher-card.component';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {TenantAccountActivityStore} from 'src/app/services/tenant/tenant-account-activity.signal-store';
import {isOpenWhileSuspended} from 'src/app/guards/suspended-tenant.guard';
import {environment} from 'src/environments/environment';
import {forkJoin} from 'rxjs';
import {startWith} from 'rxjs/operators';

import {KabeenAiService} from "../../../services/chat/kabeen-ai.service";
import {DropdownTriggerDirective} from "../../design-system/dropdown/dropdown-trigger.directive";
import {ServerService} from "../../../services/servers/server.service";
import {WorkstationConfigurationService} from "../../../services/workstations/workstation-configuration.service";
import {MobileDeviceService} from "../../../services/mobile-devices/mobile-device.service";
import {Notification, NotificationService, NotificationType} from "../../../services/notifications/notification.service";
import {OverlayscrollbarsModule} from 'overlayscrollbars-ngx';
import {PANEL_SCROLL_OPTIONS} from 'src/app/utils/scrollbars.utils';
import {NewsBadge, NewsService} from "../../../services/notifications/news.service";
import {GlobalResearchComponent} from "../../design-system/global-research/global-research.component";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {GlobalResearchShortcutService} from "../../../services/search/global-research-shortcut.service";
import {DisplayUserLogoComponent} from 'src/app/modules/design-system/display-user-logo/display-user-logo.component';
import {DropdownComponent} from 'src/app/modules/design-system/dropdown/dropdown.component';
import {TooltipDirective} from 'src/app/modules/design-system/tooltip/tooltip.component';

@Component({
	selector: 'app-main-menu',
	templateUrl: './main-menu.component.html',
	styleUrls: ['./main-menu.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, TranslatePipe, NgOptimizedImage, DisplayUserLogoComponent, DropdownComponent, TooltipDirective, DropdownTriggerDirective, OverlayscrollbarsModule, UserMenuCardComponent, TenantSwitcherCardComponent, UnavailableValueComponent, ErrorStateComponent]
})
export class MainMenuComponent implements OnInit {

	isOpen = false;
	isExpanded = false;
	isHovered = false;

	// Keyboard hint for the global-search entry: ⌘K on Apple platforms, Ctrl+K elsewhere.
	// Detected once from the visitor's OS (client-side) — mirrors the shortcut service, which
	// accepts both metaKey and ctrlKey. Computed at construction since the OS can't change.
	protected readonly researchShortcutHint =
		['macOS', 'iOS'].includes(Bowser.getParser(window.navigator.userAgent).getOSName())
			? '⌘ + K'
			: 'ctrl + K';

	// Nav area scrollbar: visible while the pointer is over the menu, fades once it leaves.
	readonly overlayScrollbarsOptions = PANEL_SCROLL_OPTIONS;

	// Timer that delays clearing `isOpen` so the collapse animation can finish.
	// Held here so it can be cancelled if the component is destroyed mid-animation
	// (otherwise the callback would touch a destroyed instance).
	private collapseTimer: ReturnType<typeof setTimeout> | null = null;

	private readonly tenantStore = inject(CurrentTenantStore);
	/** The menu lives in the shell, which only renders once the tenant is loaded. */
	protected readonly currentTenant = computed(() => this.tenantStore.currentTenant()!);
	protected readonly appsCount = this.tenantStore.applicationCount;
	// undefined while counting, null when the count failed: shown as a dash, never as 0 (KAB-2441).
	protected readonly serverCount = signal<number | null | undefined>(undefined);
	/** Workstations + mobile devices seen in the last 30 days. */
	protected readonly deviceCount = signal<number | null | undefined>(undefined);
	protected readonly currentUser = this.tenantStore.currentAccount;

	notifications: Notification[];
	unread = 0;
	/** Why the notifications could not be loaded: said in their panel, never as « no notification » (KAB-2441). */
	protected readonly notificationsError = signal<LoadError | null>(null);

	notifMenuOpened = false;

	protected readonly hasApplicationsRead = computed(() => this.tenantStore.hasPermission(Permission.APPLICATIONS_READ));
	protected readonly hasDataRead = computed(() => this.tenantStore.hasPermission(Permission.DATA_READ));
	protected readonly hasServersRead = computed(() => this.tenantStore.hasPermission(Permission.SERVERS_READ));
	protected readonly hasNetworksRead = computed(() => this.tenantStore.hasPermission(Permission.NETWORKS_READ));
	protected readonly hasOrganisationRead = computed(() => this.tenantStore.hasPermission(Permission.ORGANISATION_READ));
	protected readonly hasUserRead = computed(() => this.tenantStore.hasPermission(Permission.USERS_READ));
	protected readonly hasDevicesRead = computed(() => this.tenantStore.hasPermission(Permission.DEVICES_READ));
	/** The Infrastructure entry opens as soon as one of its sub-entries (servers, devices, networks) is readable. */
	protected readonly hasInfrastructureRead = computed(() => this.hasServersRead() || this.hasDevicesRead() || this.hasNetworksRead());
	protected readonly hasAnnouncesRead = computed(() => this.tenantStore.hasPermission(Permission.ANNOUNCES_READ));
	protected readonly hasFinanceDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.FINANCE_DASHBOARD_READ));
	protected readonly hasUsageDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.USAGE_DASHBOARD_READ));
	protected readonly hasOperationsDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.OPERATIONS_DASHBOARD_READ));
	protected readonly hasArchitectureDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.ARCHITECTURE_DASHBOARD_READ));
	protected readonly hasTechnologyDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.TECHNOLOGY_DASHBOARD_READ));
	protected readonly hasWorkstationsDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.WORKSTATIONS_DASHBOARD_READ));
	protected readonly hasCustomDashboardRead = computed(() => this.tenantStore.hasPermission(Permission.CUSTOM_DASHBOARD_READ));
	protected readonly hasFlowMappingDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.FLOW_MAPPING_DIAGRAM_READ));
	protected readonly hasCapacityMapDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.CAPACITY_MAP_DIAGRAM_READ));
	protected readonly hasApplicationMatrixDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.APPLICATION_MATRIX_DIAGRAM_READ));
	protected readonly hasApplicationLifecycleDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.APPLICATION_LIFECYCLE_DIAGRAM_READ));
	protected readonly hasApplicationComparisonDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.APPLICATION_COMPARISON_DIAGRAM_READ));
	protected readonly hasApplicationQuadrantDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.APPLICATION_QUADRANT_DIAGRAM_READ));
	protected readonly hasNetworkMappingDiagramRead = computed(() => this.tenantStore.hasPermission(Permission.NETWORK_MAPPING_DIAGRAM_READ));
	protected readonly hasAgentRead = computed(() => this.tenantStore.hasPermission(Permission.AGENTS_READ));
	protected readonly hasChatUse = computed(() => this.tenantStore.hasPermission(Permission.CHAT_USE));
	isWorkstationsDashboardFlagEnabled = false;
	isPrismFlagEnabled = false;

	protected readonly environment = environment;
	protected readonly HOME_URL = HOME_URL;
	protected readonly PRISM_URL = PRISM_URL;
	protected readonly OPERATION_URL = OPERATION_URL;
	protected readonly FINANCE_URL = FINANCE_URL;
	protected readonly USAGE_URL = USAGE_URL;
	protected readonly ARCHITECTURE_URL = ARCHITECTURE_URL;
	protected readonly TECHNOLOGY_URL = TECHNOLOGY_URL;
	protected readonly DIAGRAMS_URL = DIAGRAMS_URL;
	protected readonly ANNOUNCEMENT_URL = ANNOUNCEMENT_URL;
	protected readonly ORGANIZATION_URL = ORGANIZATION_URL;
	protected readonly USERS_USERS_URL = USERS_USERS_URL;
	protected readonly APPLICATIONS_URL = APPLICATIONS_URL;
	protected readonly DATA_URL = DATA_URL;
	protected readonly INFRASTRUCTURE_URL = INFRASTRUCTURE_URL;
	protected readonly INFRASTRUCTURE_SERVERS_URL = INFRASTRUCTURE_SERVERS_URL;
	protected readonly DEVICES_URL = DEVICES_URL;
	protected readonly DEVICES_WORKSTATIONS_URL = DEVICES_WORKSTATIONS_URL;
	protected readonly INFRASTRUCTURE_NETWORKS_URL = INFRASTRUCTURE_NETWORKS_URL;
	protected readonly EXTENSION_URL = EXTENSION_URL;
	protected readonly INTERCOM_URL = INTERCOM_URL;
	protected readonly SETTINGS_URL = SETTINGS_URL;
	protected readonly SETTINGS_ACCOUNT_URL = SETTINGS_ACCOUNT_URL;

	public translateService = inject(TranslateService);
	/** KAB-2399 — every entry but home, support and settings is locked while the workspace is suspended. */
	protected readonly suspended = this.tenantStore.isSuspended;
	/** KAB-2397 — the profile dot: green while the user is active, grey after 5 idle minutes. */
	protected readonly selfActive = inject(TenantAccountActivityStore).selfActive;
	private destroyRef = inject(DestroyRef);
	private menuStateService = inject(MenuStateService);
	protected readonly layout = inject(LayoutStore);

	/** Vrai le temps d'un tick après un franchissement du breakpoint (voir l'effect ci-dessous). */
	protected readonly viewportJump = signal(false);

	// Sous 960px le menu vit en overlay toujours déplié : le rail replié
	// (60px, dont le bouton déplier n'apparaît qu'au survol) n'a pas
	// d'équivalent tactile.
	private readonly applyOnViewportChange = effect(() => {
		this.layout.isHandset();
		// Le retour en desktop rejoue la préférence repliée : sans ce gel, la carte
		// glisserait de 255 à 60px avec sa transition, ce qui se lit comme une
		// fermeture de menu alors que l'utilisateur n'a fait que redimensionner.
		this.viewportJump.set(true);
		this.applyMenuState(untracked(this.menuStateService.state), true);
		setTimeout(() => this.viewportJump.set(false));
	});

	// Chaque repli / dépliage du service, animé.
	private readonly followMenuState = effect(() => {
		const state = this.menuStateService.state();
		untracked(() => this.applyMenuState(state));
	});
	private intercomService = inject(IntercomService);
	private router = inject(Router);
	private featureFlagService = inject(FeatureFlagService);
	private kabeenAiService = inject(KabeenAiService);
	/** Prism's entry shows until the settings say otherwise. */
	protected readonly isChatEnabled = computed(() => this.kabeenAiService.settings()?.chatEnabled ?? true);
	private serverService = inject(ServerService);
	private workstationConfigurationService = inject(WorkstationConfigurationService);
	private mobileDeviceService = inject(MobileDeviceService);
	private notificationService = inject(NotificationService);
	private newsService = inject(NewsService);
	private readonly relevantNewsBadge = toSignal(this.newsService.relevantNewsBadge$, {initialValue: [] as NewsBadge[]});
	protected readonly showNewsBadge = computed(() => this.relevantNewsBadge().some(badge =>
		badge.route === WORKSTATIONS_DASHBOARD_URL
		&& !this.newsService.visitedBadge().includes(badge.id)
		&& (!badge.permission || this.tenantStore.hasPermission(badge.permission))));
	private dialog = inject(MatDialog);
	private dialogRef: MatDialogRef<GlobalResearchComponent> | undefined;
	private researchShortcut: GlobalResearchShortcutService = inject(GlobalResearchShortcutService)

	ngOnInit(): void {
		this.destroyRef.onDestroy(() => {
			if (this.collapseTimer !== null) {
				clearTimeout(this.collapseTimer);
				this.collapseTimer = null;
			}
		});

		this.featureFlagService.isFeatureEnabled(FeatureFlag.PRISM)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(enabled => this.isPrismFlagEnabled = enabled);
		this.featureFlagService.isFeatureEnabled(FeatureFlag.WORKSTATIONS_DASHBOARD)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(enabled => this.isWorkstationsDashboardFlagEnabled = enabled);
		// A suspended workspace gets a 403 on its data (KAB-2399): its entries show a lock, not counts or badges.
		if (!this.suspended()) {
			const tenantId = this.currentTenant().configuration.id;
			if (this.hasServersRead()) {
				this.serverService.getTenantServerCount(tenantId)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({next: count => this.serverCount.set(count), error: () => this.serverCount.set(null)});
			}
			if (this.hasDevicesRead()) {
				forkJoin([
					this.workstationConfigurationService.getTenantWorkstationCount(tenantId),
					this.mobileDeviceService.getActiveMobileDeviceCount(tenantId)
				])
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: ([workstationCount, mobileDeviceCount]) => this.deviceCount.set(workstationCount + mobileDeviceCount),
						error: () => this.deviceCount.set(null),
					});
			}
			this.notificationService.recheckNotificationsEvent
				.pipe(startWith(true), takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.getNotifications(tenantId));
		}

	}

	getNotifications(tenantId: string) {
		this.unread = 0;
		this.notificationsError.set(null);
		this.notificationService.getNotifications(tenantId).subscribe({
			next: notifications => {
				this.notifications = notifications.notifications
					.filter(n => n.type !== NotificationType.COMMENT_ADDED)
					.map(n => {
						if (n.status === 'unread') {
							this.unread++;
						}
						n.date = new Date(n.date);
						return n;
					})
			},
			error: error => this.notificationsError.set(toLoadError(error)),
		})
	}

	protected retryNotifications(): void {
		this.getNotifications(this.currentTenant().configuration.id);
	}

	/** Workstations dashboard counts as available only when its feature flag is on too. */
	private get canAccessWorkstationsDashboard(): boolean {
		return this.hasWorkstationsDashboardRead() && this.isWorkstationsDashboardFlagEnabled;
	}

	get hasAnyDashboardRead(): boolean {
		return this.hasFinanceDashboardRead() || this.hasUsageDashboardRead() ||
			this.hasOperationsDashboardRead() || this.hasArchitectureDashboardRead() ||
			this.hasTechnologyDashboardRead() || this.canAccessWorkstationsDashboard || this.hasCustomDashboardRead();
	}

	get firstAvailableDashboardUrl(): AppUrl.AppRouteUrl | undefined {
		if (this.hasOperationsDashboardRead()) return OPERATION_URL;
		if (this.hasFinanceDashboardRead()) return FINANCE_URL;
		if (this.hasUsageDashboardRead()) return USAGE_URL;
		if (this.hasArchitectureDashboardRead()) return ARCHITECTURE_URL;
		if (this.hasTechnologyDashboardRead()) return TECHNOLOGY_URL;
		if (this.canAccessWorkstationsDashboard) return WORKSTATIONS_DASHBOARD_URL;
		if (this.hasCustomDashboardRead()) return CUSTOM_DASHBOARDS_URL;
		return undefined;
	}

	get isAnyDashboardCurrentPage(): boolean {
		return this.isCurrentPage(OPERATION_URL) || this.isCurrentPage(FINANCE_URL) ||
			this.isCurrentPage(USAGE_URL) || this.isCurrentPage(ARCHITECTURE_URL) ||
			this.isCurrentPage(TECHNOLOGY_URL) || this.isCurrentPage(WORKSTATIONS_DASHBOARD_URL) ||
			this.isCurrentPage(CUSTOM_DASHBOARDS_URL);
	}

	get hasAnyDiagramRead(): boolean {
		return this.hasFlowMappingDiagramRead() || this.hasCapacityMapDiagramRead() ||
			this.hasApplicationMatrixDiagramRead() || this.hasApplicationLifecycleDiagramRead() ||
			this.hasApplicationComparisonDiagramRead() || this.hasNetworkMappingDiagramRead() ||
			this.hasApplicationQuadrantDiagramRead();
	}

	get tenantLogoUrl(): string | null {
		return this.currentTenant().configuration.publicLogoUrl ?? null;
	}

	get userFullName(): string {
		const account = this.currentUser()?.account;
		if (!account) return '';
		return `${account.firstName ?? ''} ${account.lastName ?? ''}`.trim();
	}

	get userEmail(): string {
		return this.currentUser()?.account?.email ?? '';
	}

	isCurrentPage(url: string): boolean {
		return this.router.url.startsWith(url);
	}

	goTo(url: AppUrl.AppRouteUrl | `https://${string}` | `http://${string}` | undefined): void {
		if (!url) return;
		if (this.suspended() && !url.startsWith(INTERCOM_URL) && !isOpenWhileSuspended(url)) return;
		if (url.trim().startsWith('https') || url.trim().startsWith('http')) {
			window.open(url, '_blank');
		} else if (url.trim().startsWith(INTERCOM_URL)) {
			this.intercomService.toggle();
		} else {
			this.intercomService.hide();
			this.router.navigate([url], {queryParamsHandling: 'preserve', queryParams: {tenant: this.tenantStore.currentTenantId()}});
		}
	}

	openResearchBar(): void {
		this.researchShortcut.toggle()
	}

	toggleMenu(): void {
		if (this.isOpen) {
			this.menuStateService.closeMenu();
		} else {
			this.menuStateService.openMenu();
		}
	}

	// En overlay (< 960px) le bouton du header ferme l'overlay au lieu de
	// replier le menu — le repli n'existe pas au tactile.
	protected onToggleClick(): void {
		if (this.layout.isHandset()) {
			this.menuStateService.closeMobileMenu();
		} else {
			this.toggleMenu();
		}
	}

	// Sous 960px, Infrastructure se déplie en accordéon (voir template) au
	// lieu d'ouvrir le dropdown flottant — inutilisable sur petit écran.
	protected infraAccordionOpen = false;

	protected onInfraClick(): void {
		if (this.layout.isHandset() && this.hasInfrastructureRead() && !this.suspended()) {
			this.infraAccordionOpen = !this.infraAccordionOpen;
		}
	}

	/**
	 * `immediate` : le repli n'est pas animé (franchissement du breakpoint), donc
	 * pas de sursis avant de rendre le header replié — sinon il resterait 300ms
	 * dans sa version dépliée, rognée par une carte déjà revenue à 60px.
	 */
	private applyMenuState(state: MenuState, immediate = false): void {
		if (state.open || this.layout.isHandset()) {
			if (this.collapseTimer !== null) {
				clearTimeout(this.collapseTimer);
				this.collapseTimer = null;
			}
			this.isExpanded = true;
			this.isOpen = true;
		} else {
			this.isExpanded = false;
			if (this.collapseTimer !== null) {
				clearTimeout(this.collapseTimer);
				this.collapseTimer = null;
			}
			if (immediate) {
				this.isOpen = false;
				return;
			}
			this.collapseTimer = setTimeout(() => {
				this.isOpen = false;
				this.collapseTimer = null;
			}, 300);
		}
	}

	onMenuHover(): void {
		this.isHovered = true;
	}

	onMenuLeave(): void {
		this.isHovered = false;
	}

	redirectToNotificationEntity(notification: Notification) {
		switch (notification.type) {
			case NotificationType.COMMENT_ADDED:
			case NotificationType.USER_TAGGED_IN_COMMENT:
				this.router.navigate([APPLICATIONS_URL], {queryParams: {applicationId: notification.applicationId, comments: true}, queryParamsHandling: "merge"});
				break;
			default:
				break;
		}
		//this.router.navigate([notification.entityLink]);
	}
}
